/**
 * The look-and-feel options a presentation type declares, read from its own
 * schema (CT-5579).
 *
 * Every type publishes `configSchema.properties.display` beside the `bindings`
 * the builder already reads — 167 properties across the 28 types, from a KPI's
 * `show_trend_icon` and `comparison_mode` to a table's `density` and a donut's
 * `inner_radius_pct`. Nothing rendered any of them: the Configure step's
 * SETTINGS block is a fixed pair of controls shared by all 28, so a KPI offered
 * no KPI setting at all.
 *
 * Persistence was never the gap. `visualizationConfig.display` round-trips
 * through `create`, `publish` and the read intact, nested objects included —
 * measured on dev 2026-10-07. What was missing was the form.
 *
 * ## What this renders, and what it leaves alone
 *
 * Scalars become controls: `enum` a select, `boolean` a toggle, `integer` and
 * `number` a numeric field, `string` a text input. An object whose own
 * properties are scalars becomes a group of those — `empty_state` (on all 28),
 * `legend`, `pagination`, `format` — resolved through `$defs` when the schema
 * refers to them by `$ref`.
 *
 * Four shapes are deliberately skipped, and `skipped` names them so the gap is
 * visible rather than silent:
 *
 * - **Lists of objects** — `thresholds` (up to 10 of label/value/operator/
 *   colour), `lanes`, `stage_order_static`. Each is an editor of its own, not
 *   a field.
 * - **Free-form maps** — `type_icons`, `severity_map`, `initial_view`. The
 *   schema states no inner shape, so there is nothing to render from.
 * - **Field references** — `sort` and `default_sort` carry a `field_key` that
 *   must name a query alias. A text box there invites a value that fails at
 *   publish; it wants the alias picker the Data step already builds, which is
 *   a follow-up rather than a text input shipped today.
 * - **Unresolved `$ref`s** — `color_token` resolves to nothing in `$defs`.
 *
 * ## Labels are not in the schema
 *
 * Not one of the 167 properties carries a `title`, and only two carry a
 * `description`. So the key is humanised the way `BindingSlotRow` humanises a
 * slot — `show_trend_icon` → "Show trend icon" — and an i18n entry overrides it
 * where one exists. A property Data adds tomorrow renders with a readable
 * English label and no translation, which is worse than a translated one and
 * much better than not appearing. Asking Data for `title`/`description` is the
 * real fix and belongs in the same conversation as the casing of LIST and
 * PROFILE_CARD.
 *
 * ## No defaults either
 *
 * No property declares a `default`, so the builder cannot know what the backend
 * assumes when one is absent. Nothing is pre-filled and nothing untouched is
 * sent: a setting the author never opened stays out of the payload, where the
 * backend's own default governs it. That is why `toDisplayConfig` drops empties
 * rather than writing a value it guessed.
 */

/** Only an object this shallow becomes a group of controls. */
const SCALAR_TYPES = new Set(['boolean', 'integer', 'number', 'string']);

/** Slots whose value names a query alias — see the note above. */
const FIELD_REFERENCE_KEYS = new Set(['sort', 'default_sort']);

const resolveRef = (node, configSchema) => {
  const ref = node?.$ref;
  if (!ref) return node;
  const name = ref.split('/').pop();
  return configSchema?.$defs?.[name] ?? null;
};

/** The control a scalar node wants, or `null` when it is not a scalar. */
const controlFor = (node) => {
  if (Array.isArray(node?.enum) && node.enum.length) return 'select';
  if (node?.type === 'boolean') return 'toggle';
  if (node?.type === 'integer' || node?.type === 'number') return 'number';
  if (node?.type === 'string') return 'text';
  return null;
};

const fieldFrom = (path, key, node, group) => ({
  path,
  key,
  group,
  control: controlFor(node),
  options: node.enum ?? null,
  maxLength: node.maxLength ?? null,
  min: node.minimum ?? null,
  max: node.maximum ?? null,
  // Six properties across seven types state one: a currency is three capitals,
  // an action code is `^[A-Z0-9_]{1,100}$`, a language is `en` or `en-GB`.
  // A length cap and a numeric range can be enforced as the author types; a
  // shape cannot, so it is checked when they leave the field.
  pattern: typeof node.pattern === 'string' ? node.pattern : null,
});

/**
 * @param {object|null} configSchema The type's `configSchema`, as the catalog
 *   publishes it.
 * @returns {{ fields: Array, skipped: string[] }} `fields` in the schema's own
 *   order, each with the control it wants and the path it writes to;
 *   `skipped` lists the properties this cannot render yet.
 */
export function readDisplaySettings(configSchema) {
  const display = configSchema?.properties?.display;
  const properties = display?.properties;
  if (!properties) return { fields: [], skipped: [] };

  const fields = [];
  const skipped = [];

  Object.entries(properties).forEach(([key, raw]) => {
    if (FIELD_REFERENCE_KEYS.has(key)) {
      skipped.push(key);
      return;
    }

    const node = resolveRef(raw, configSchema);
    if (!node) {
      skipped.push(key);
      return;
    }

    const control = controlFor(node);
    if (control) {
      fields.push(fieldFrom(key, key, node, null));
      return;
    }

    // One level down: an object of scalars is a group of controls.
    if (node.type === 'object' && node.properties) {
      const children = Object.entries(node.properties)
        .filter(([, child]) => SCALAR_TYPES.has(child?.type) || Array.isArray(child?.enum))
        .map(([childKey, child]) => fieldFrom(`${key}.${childKey}`, childKey, child, key));

      if (children.length) fields.push(...children);
      else skipped.push(key);
      return;
    }

    skipped.push(key);
  });

  return { fields, skipped };
}

/**
 * Does this value have the shape its property asks for?
 *
 * Permissive by default, on the same reasoning as `columnFitsSlot`: a property
 * with no `pattern`, a value not yet typed, and a `pattern` this engine cannot
 * compile all pass. A regex the catalog wrote for Postgres that JavaScript
 * refuses is not the author's problem, and refusing their input over it would
 * be a dead end they cannot argue with — the backend still has the final say
 * at publish.
 *
 * @param {string|number|boolean|undefined} value
 * @param {string|null} pattern The property's `pattern`, as the schema states it.
 */
export function matchesPattern(value, pattern) {
  if (!pattern) return true;
  if (value === '' || value === null || value === undefined) return true;
  try {
    return new RegExp(pattern).test(String(value));
  } catch {
    return true;
  }
}

/**
 * The flat form state, nested the way `visualizationConfig.display` wants it.
 *
 * An empty string is "not set", not an empty caption: the author who clears a
 * text box is removing their override, and sending `""` would overwrite the
 * backend's default with nothing. A group whose every child is unset vanishes
 * with them, so `display` itself is omitted when nothing was touched.
 *
 * @param {object} values `{ path: value }`, paths as `readDisplaySettings` emits.
 * @returns {object|null} The nested object, or `null` when nothing is set.
 */
export function toDisplayConfig(values = {}) {
  const display = {};

  Object.entries(values).forEach(([path, value]) => {
    if (value === '' || value === null || value === undefined) return;

    const [head, child] = path.split('.');
    if (!child) {
      display[head] = value;
      return;
    }
    display[head] = { ...(display[head] ?? {}), [child]: value };
  });

  return Object.keys(display).length ? display : null;
}

/**
 * `visualizationConfig.display` flattened back into form state, for editing a
 * widget that already has one.
 *
 * @param {object|null} display The stored object.
 * @returns {object} `{ path: value }`, matching `readDisplaySettings`' paths.
 */
export function fromDisplayConfig(display) {
  if (!display || typeof display !== 'object') return {};

  const values = {};
  Object.entries(display).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(value).forEach(([childKey, childValue]) => {
        if (childValue !== null && childValue !== undefined) values[`${key}.${childKey}`] = childValue;
      });
      return;
    }
    if (value !== null && value !== undefined) values[key] = value;
  });

  return values;
}

export default readDisplaySettings;
